<script setup>
const { model } = defineProps({
  model: { type: Object, required: true },
})

const banner = computed(() => resolveProductBanner(model.productTag))

const title = computed(() =>
  model.title?.fragments?.[0]?.internalFormat ?? model.title,
)

// Everything below is bound in this template, never inside NuxtImg/NuxtLink or through
// :class: hydration corrects only template-bound attributes, so an HTML page cached
// before a listing change would otherwise keep the old product's image, link and banner
// colour in each slot. The image options mirror what NuxtImg sent.
const $img = useImage()
const image = computed(() => {
  const src = model.imageUrl || '/images/coming_soon.png'
  const modifiers = { width: 500, height: 500, quality: $img.options.quality }
  const options = { provider: 'fastlyAprimo' }
  return {
    src: $img(src, modifiers, options),
    srcset: $img.getSizes(src, { ...options, modifiers }).srcset,
  }
})
</script>

<template>
  <NuxtLink
    v-slot="{ href, navigate, prefetch, shouldPrefetch }"
    :to="useCmsUrl(model.path + (model.path.endsWith('/') ? '' : '/') + model.url)"
    custom
  >
    <a
      :href="href"
      class="block"
      @click="navigate"
      @pointerenter="shouldPrefetch('interaction') && prefetch()"
      @focus="shouldPrefetch('interaction') && prefetch()"
    >
      <div class="relative block rounded-xl w-full card-shadow bg-white mb-3 aspect-square 2xl:mb-5">
        <img
          :src="image.src"
          :srcset="image.srcset"
          class="rounded-xl w-full aspect-square object-contain"
          :alt="model.imageAltText"
          width="500"
          height="500"
          loading="lazy"
        >
        <div
          v-if="banner"
          :data-variant="banner.variant"
          class="absolute top-3 left-3 rounded-full px-3 py-1 text-sm font-semibold leading-none shadow-sm text-white data-[variant=new]:bg-med-navy data-[variant=bestseller]:bg-med-orange"
        >
          {{ banner.label }}
        </div>
      </div>
      <span
        class="underline font-semibold text-[15px] 2xl:text-lg"
        v-html="title"
      />
    </a>
  </NuxtLink>
</template>
